import { ETooltipIntents, ETypographyIntents } from '@src/typings';
import { inputVariants } from '@src/utilities/inputVariants';
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { Input as InputAntD, type InputRef } from 'antd';
import { ForwardedRef, forwardRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { withTheme } from '../ThemeProvider';
import { Tooltip } from '../Tooltip';
import {
    StyledError,
    StyledInputWrapper,
    StyledLabel,
    StyledRequiredMark,
} from './styles';
import { PasswordInputProps } from './types';

const PasswordInputComponent = forwardRef(
    (
        {
            intent,
            size,
            className,
            label,
            error,
            isRequired,
            help,
            onChange,
            ...props
        }: PasswordInputProps,
        ref: ForwardedRef<InputRef>
    ) => {
        const [value, setValue] = useState<string>(
            props.value?.toString?.() ?? ''
        );

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            setValue(e.target.value);
            onChange?.(e);
        };

        return (
            <StyledInputWrapper>
                {(label || help) && (
                    <div className="flex w-full items-center justify-between mb-2.5">
                        <StyledLabel
                            intent={ETypographyIntents.Regular}
                            className="flex items-center justify-start gap-x-1"
                        >
                            <span>{label}</span>
                            {isRequired && (
                                <StyledRequiredMark>*</StyledRequiredMark>
                            )}
                        </StyledLabel>
                        {help && (
                            <Tooltip
                                title={help}
                                color="secondary"
                                intent={ETooltipIntents.Question}
                            />
                        )}
                    </div>
                )}
                <InputAntD.Password
                    ref={ref}
                    status={error ? 'error' : props.status}
                    variant={intent}
                    value={value}
                    onChange={handleChange}
                    visibilityToggle={true}
                    iconRender={(visible) => {
                        if (value.length === 0) return null;
                        return visible ? (
                            <EyeInvisibleOutlined />
                        ) : (
                            <EyeOutlined />
                        );
                    }}
                    className={twMerge(
                        inputVariants({
                            className,
                            intent,
                            size,
                            error: Boolean(error),
                        })
                    )}
                    {...props}
                />
                {error && (
                    <StyledError
                        intent={ETypographyIntents.Badge}
                        className="mt-1"
                    >
                        {error}
                    </StyledError>
                )}
            </StyledInputWrapper>
        );
    }
);

PasswordInputComponent.displayName = 'InputComponent';

export const PasswordInput = withTheme(PasswordInputComponent);
